import React from 'react';
import { APITypes, OrganizationTypes } from '../../../../primary-record-types';
import { FormStack } from '../../../v2BaseComponents/Stack';
import { Field } from 'formik';
import { FormikInputs } from '../../../v2BaseComponents/Inputs';
import { composeValidators, email, futureDate, getDateFormatValidator, required } from '../../../../utils/validators';
import { getIntl } from '../../../../utils/intl';
import { SelectOption } from '../../../v2BaseComponents/Inputs/Select/types';
import { FormDirtyStateTracker } from '../../FormExitValidation';
import { CenteredFormRow, SplitFormRow } from '../../../v2BaseComponents/Form';
import useFeatureToggles from '../../../../hooks/api/useFeatureToggles';
import { useLoadedUser } from '../../../../hooks/api/useUser';

const intl = getIntl('organization-client-invitation-fields', {
    organizationInvitationDefinitionId: 'Invitation Template',
    clientOrganizerEmail: 'Point of Contact Email',
    clientOrganizerFirstName: 'Point of Contact First Name',
    clientOrganizerLastName: 'Point of Contact Last Name',
    clientFirstName: 'Client First Name',
    clientLastName: 'Client Last Name',
    clientDOB: 'Client Date of Birth',
    endDate: 'End Date',
    endDateProvisionedProfileDisclaimer:
        'The provisioned medical profile will be deleted if invitation is not accepted before the end date.',
    provisionMedicalProfile: 'Create Medical Profile Before Acceptance?',
    provisionMedicalProfileHelpText:
        // TODO: Add help docs link once it exists
        "Create a medical profile for the client before they accept the invitation. This will allow you to pre-populate the client's medical profile with data while the invitation is pending. Leave unchecked if the client already uses Primary Record.",
    youCannotInviteYourselfOrOtherProfessionalUsers:
        'You cannot send a client invite to yourself or other professional users.',
    disableProvisionProfileExistingInvitation: 'This option cannot be modified after the invitation has been created.',
    disableProvisionProfileRejectedClient: 'This option cannot be selected when resending a rejected invitation.',
    disableProvisionProfilePreviousClient: 'This option cannot be selected when inviting a previous client.',
});

export type OrganizationClientInvitationDisableProvisionProfileReason =
    | 'inviting-rejected-client'
    | 'inviting-previous-client'
    | 'editing-invitation';

export type OrganizationClientInvitationFieldsOwnProps = {
    invitationDefinitions: APITypes.organizationService.ListDefinitionsResponse['invitationDefinitions'];
    value: FormValue;
    disableProvisionProfileReason?: OrganizationClientInvitationDisableProvisionProfileReason;
};

export type OrganizationClientInvitationFieldsProps = OrganizationClientInvitationFieldsOwnProps;

export type FormValue = APITypes.organizationService.CreateOrganizationClientInvitationRequest;

const getDefaultOrganizationDefinitionId = (
    invitationDefinitions: APITypes.organizationService.ListDefinitionsResponse['invitationDefinitions'],
): string => {
    return invitationDefinitions?.length === 1 ? invitationDefinitions[0].organizationInvitationDefinitionId : '';
};

export const getDefaultFormValue = (
    invitationDefinitions: APITypes.organizationService.ListDefinitionsResponse['invitationDefinitions'],
): FormValue => ({
    organizationInvitationDefinitionId: getDefaultOrganizationDefinitionId(invitationDefinitions),
    clientOrganizerEmail: '',
    clientOrganizerFirstName: '',
    clientOrganizerLastName: '',
    clientFirstName: '',
    clientLastName: '',
    clientDOB: '',
    requestedProfilePermissions: null,
    expirationDateTime: undefined,
    provisionMedicalProfile: false,
});

const getValidatedEndDate = (date: string | undefined): string | undefined => {
    if (!date) return;
    const datePart = date.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
    if (!datePart) return;

    // Always set to 12PM UTC
    return `${datePart}T12:00:00Z`;
};

export const formValueToCreateClientRecordInvitationRequest = (
    formValue: FormValue,
): APITypes.organizationService.CreateOrganizationClientInvitationForOrganizationClientRecordRequest => {
    return {
        organizationInvitationDefinitionId: formValue.organizationInvitationDefinitionId,
        clientDetails: {
            firstName: formValue.clientFirstName,
            lastName: formValue.clientLastName,
            dob: formValue.clientDOB,
        },
        clientOrganizerDetails: {
            firstName: formValue.clientOrganizerFirstName,
            lastName: formValue.clientOrganizerLastName,
            email: formValue.clientOrganizerEmail,
        },
        requestedProfilePermissions: null,
        expirationDateTime: formValue.provisionMedicalProfile
            ? undefined
            : getValidatedEndDate(formValue.expirationDateTime),
        provisionMedicalProfile: !!formValue.provisionMedicalProfile,
    };
};

export const clientRecordToFormValue = (
    clientRecord:
        | OrganizationTypes.OrganizationClientRecord
        | APITypes.organizationService.OrganizationClientRecordDetails,
    invitationDefinitions: APITypes.organizationService.ListDefinitionsResponse['invitationDefinitions'],
): FormValue => {
    const defaultOrganizationDefinitionId =
        invitationDefinitions?.length === 1 ? invitationDefinitions[0].organizationInvitationDefinitionId : '';

    return {
        organizationInvitationDefinitionId: defaultOrganizationDefinitionId,
        clientOrganizerEmail: clientRecord.clientOrganizerDetails.email || '',
        clientOrganizerFirstName: clientRecord.clientOrganizerDetails.firstName || '',
        clientOrganizerLastName: clientRecord.clientOrganizerDetails.lastName || '',
        clientFirstName: clientRecord.clientDetails.firstName || '',
        clientLastName: clientRecord.clientDetails.lastName || '',
        clientDOB: clientRecord.clientDetails.dob || '',
        requestedProfilePermissions: null,
        expirationDateTime: undefined,
        provisionMedicalProfile: !!(clientRecord as any)?.provisionedMedicalProfileId,
    };
};

export const clientInvitationToFormValue = (
    invitation: APITypes.organizationService.OrganizationClientInvitationDetails,
    provisionMedicalProfile: boolean,
): FormValue => ({
    organizationInvitationDefinitionId: invitation.organizationInvitationDefinitionId,
    clientOrganizerEmail: invitation.clientOrganizerEmail,
    clientOrganizerFirstName: invitation.clientOrganizerFirstName,
    clientOrganizerLastName: invitation.clientOrganizerLastName,
    clientFirstName: invitation.clientFirstName,
    clientLastName: invitation.clientLastName,
    clientDOB: invitation.clientDOB,
    requestedProfilePermissions: null,
    expirationDateTime: invitation.expirationDateTime,
    provisionMedicalProfile: !!provisionMedicalProfile,
});

const OrganizationClientInvitationFields: React.FC<OrganizationClientInvitationFieldsProps> = (props) => {
    const { user } = useLoadedUser({});
    const hasProvisionedMedicalProfilesToggle =
        !!useFeatureToggles().featureToggles.organizationProvisionedMedicalProfiles;
    const invitationDefinitionSelectOptions = props.invitationDefinitions
        .map<SelectOption>((definition) => ({
            label: definition.name,
            value: definition.organizationInvitationDefinitionId,
            testID: definition.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    let provisionProfileWarnText: string | undefined;
    if (props.disableProvisionProfileReason === 'editing-invitation') {
        provisionProfileWarnText = intl('disableProvisionProfileExistingInvitation');
    } else if (props.disableProvisionProfileReason === 'inviting-previous-client') {
        provisionProfileWarnText = intl('disableProvisionProfilePreviousClient');
    } else if (props.disableProvisionProfileReason === 'inviting-rejected-client') {
        provisionProfileWarnText = intl('disableProvisionProfileRejectedClient');
    }

    return (
        <FormStack>
            <SplitFormRow>
                <Field
                    name="organizationInvitationDefinitionId"
                    title={intl('organizationInvitationDefinitionId')}
                    component={FormikInputs.Select}
                    selectOptions={invitationDefinitionSelectOptions}
                    validate={required}
                    isRequired
                />
            </SplitFormRow>
            <CenteredFormRow>
                <Field
                    name="clientOrganizerEmail"
                    title={intl('clientOrganizerEmail')}
                    component={FormikInputs.Input}
                    validate={composeValidators(required, email, (value) => {
                        if (typeof value !== 'string' || !value || value !== user?.email) return;

                        return intl('youCannotInviteYourselfOrOtherProfessionalUsers');
                    })}
                    isRequired
                    forceFullWidth
                />
            </CenteredFormRow>
            <SplitFormRow>
                <Field
                    name="clientOrganizerFirstName"
                    title={intl('clientOrganizerFirstName')}
                    component={FormikInputs.Input}
                    validate={required}
                    isRequired
                />
                <Field
                    name="clientOrganizerLastName"
                    title={intl('clientOrganizerLastName')}
                    component={FormikInputs.Input}
                    validate={required}
                    isRequired
                />
            </SplitFormRow>
            <SplitFormRow>
                <Field
                    name="clientFirstName"
                    title={intl('clientFirstName')}
                    component={FormikInputs.Input}
                    validate={required}
                    isRequired
                />
                <Field
                    name="clientLastName"
                    title={intl('clientLastName')}
                    component={FormikInputs.Input}
                    validate={required}
                    isRequired
                />
            </SplitFormRow>
            <SplitFormRow>
                <Field
                    name="clientDOB"
                    title={intl('clientDOB')}
                    component={FormikInputs.DateTimePicker}
                    validate={composeValidators(required, getDateFormatValidator('MM/DD/YYYY'))}
                    isRequired
                />
            </SplitFormRow>
            {hasProvisionedMedicalProfilesToggle ? (
                <SplitFormRow>
                    <Field
                        name="provisionMedicalProfile"
                        title={intl('provisionMedicalProfile')}
                        component={FormikInputs.Checkbox}
                        isRequired
                        isDisabled={!!props.disableProvisionProfileReason}
                        helpText={intl('provisionMedicalProfileHelpText')}
                        warnText={provisionProfileWarnText}
                    />
                </SplitFormRow>
            ) : null}
            {props.value.provisionMedicalProfile ? null : (
                <SplitFormRow>
                    <Field
                        name="expirationDateTime"
                        title={intl('endDate')}
                        component={FormikInputs.DateTimePicker}
                        validate={composeValidators(getDateFormatValidator('MM/DD/YYYY'), futureDate)}
                        helpText={
                            props.value.provisionMedicalProfile
                                ? intl('endDateProvisionedProfileDisclaimer')
                                : undefined
                        }
                    />
                </SplitFormRow>
            )}
            <FormDirtyStateTracker />
        </FormStack>
    );
};

export default React.memo(OrganizationClientInvitationFields);
